import { supabase } from "@/integrations/supabase/client";

export type NotificationType = "guest_invite" | string;

export type NotificationRow = {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  payload: any;
  read_at: string | null;
  created_at: string;
};

export async function getUnreadNotificationCount(
  userId: string,
): Promise<number> {
  const { count, error } = await (supabase as any)
    .from("notifications")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .is("read_at", null);

  if (error) {
    console.error("Failed to fetch unread notification count:", error);
    return 0;
  }
  return count ?? 0;
}

export async function listNotifications(
  userId: string,
  limit: number = 30,
): Promise<NotificationRow[]> {
  const { data, error } = await (supabase as any)
    .from("notifications")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Failed to list notifications:", error);
    return [];
  }
  return (data || []) as NotificationRow[];
}

export async function markNotificationRead(
  notificationId: string,
): Promise<boolean> {
  const { error } = await (supabase as any)
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("id", notificationId);

  if (error) {
    console.error("Failed to mark notification read:", error);
    return false;
  }
  return true;
}

export async function markNotificationReadWithStatus(
  notificationId: string,
  status: "accepted" | "declined",
  currentPayload: any,
): Promise<boolean> {
  const { error } = await (supabase as any)
    .from("notifications")
    .update({
      read_at: new Date().toISOString(),
      payload: { ...(currentPayload ?? {}), status },
    })
    .eq("id", notificationId);

  if (error) {
    console.error("Failed to mark notification read with status:", error);
    return false;
  }
  return true;
}

export async function deleteNotifications(
  notificationIds: string[],
  userId: string,
): Promise<boolean> {
  if (notificationIds.length === 0) return true;

  const { data, error } = await (supabase as any)
    .from("notifications")
    .delete()
    .in("id", notificationIds)
    .eq("user_id", userId) // extra protection
    .select(); // REQUIRED

  console.log("Deleted rows:", data);

  if (error) {
    console.error("Failed to delete notifications:", error);
    return false;
  }

  // Ensure rows were actually deleted
  return (data?.length ?? 0) > 0;
}
